import jsPDF from 'jspdf';
import 'jspdf-autotable';
import {observer} from 'mobx-react';
import moment from 'moment';
import React, {useState} from 'react';

import {useFormatDate} from '@udemy/i18n';
import {useFormatNumber} from '@udemy/i18n';
import {Icon} from '@udemy/icons';
import {IconButton} from '@udemy/react-core-components';
import {Pagination} from '@udemy/react-navigation-components';
import {MainContentLoader} from '@udemy/react-reveal-components';
import {Table} from '@udemy/react-structure-components';
import {AuthenticatedUser, useUDData} from '@udemy/ud-data';

import {notoSansRegularBase64} from '../../assets/font/base64Fonts';
import udemyLogo from '../../assets/images/udemy_logo.png';
import {PayoutStatus} from '../../features/payout-report/constants';
import {useInstructorPayoutsByPageQuery} from '../../generated/graphql';
import {PayoutStore} from '../../stores/payout-store';
import {PayoutTableItem} from '../../types/payout-report-types';
import {PayoutMethodMapping} from './payout-method-mapping';
import {PayoutStatusBadge} from './payout-status-badge';
import styles from './section.module.less';
export const PayoutHistorySection = observer(() => {
    const {formatNumber} = useFormatNumber();
    const {toLocaleDateString} = useFormatDate();

    const [store] = useState(() => {
        return new PayoutStore();
    });

    const instructorPayoutsInfoPage = useInstructorPayoutsByPageQuery({
        page: store.currentPage - 1,
    });

    const handlePageChange = (page: number) => {
        store.currentPage = page;
    };
    const {me} = useUDData();
    const getLastPageNoteOldPayoutData = (
        hasOlderPayouts: boolean,
        totalPage: number,
        currentPage: number,
    ) => {
        if (hasOlderPayouts && totalPage === currentPage)
            return (
                <div className={styles['info-container']}>
                    <b>
                        {gettext(
                            'Please note that the data provided is only available for the payouts following May 2018.',
                        )}
                    </b>
                </div>
            );
        else return null;
    };

    const downloadPayslip = (row: PayoutTableItem) => {
        const doc = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: 'a4',
        });
        // Adding the logo
        const udemyLogoImage = udemyLogo.src;
        doc.addImage(udemyLogoImage, 'PNG', 10, 10, 80, 40); // Properly sized logo
        const addressText =
            'Udemy Inc.\n600 Harrison Street, 3rd Floor\nSan Francisco, CA 94107\nUnited States';
        doc.addFileToVFS('NotoSansJP-Regular.ttf', notoSansRegularBase64);
        doc.addFont('NotoSansJP-Regular.ttf', 'NotoSansJPRegular', 'normal');

        doc.setFontSize(10);
        doc.setTextColor(100);
        doc.text(addressText, 20, 45);
        // Setting font
        doc.setFont('Helvetica', 'bold');
        doc.setTextColor(0, 0, 0);

        // Main Title with Color
        doc.setFontSize(20);
        doc.text('Payment Report', 105, 70, {align: 'center'});
        // Define the table columns and row
        const authenticatedUser = me as AuthenticatedUser;
        const instructorName = authenticatedUser.display_name;
        const instructorId = row.instructor.id;
        const earningPeriodsString = getEarningPeriodsString(row.earningPeriods);
        const description = 'Instructor Revenue for ' + earningPeriodsString;
        const payoutDate = moment(row.payoutDate).format('YYYY-MM-DD');
        const grossAmount = formatNumber((row.amount + row.taxAmount).toFixed(2));
        const taxAmount = formatNumber(row.taxAmount.toFixed(2));
        const netAmount = formatNumber(row.amount.toFixed(2));
        const payoutMethod = row.paymentMethodDisplayName;
        const rows = [
            ['Instructor Name', instructorName],
            ['Instructor Id', instructorId],
            ['Description', description],
            ['Estimated Payment Date', payoutDate],
            ['Gross Amount', '$' + grossAmount],
            ['Withholding Tax', '$' + taxAmount],
            ['Net Amount', '$' + netAmount],
            ['Payment Vendor', payoutMethod],
        ];

        // Inserting the autoTable
        doc.autoTable({
            body: rows,
            startY: 80,
            styles: {fillColor: [240, 240, 240]}, // light gray background for header
            columnStyles: {
                0: {fillColor: [255, 255, 255], textColor: [0, 0, 0], fontStyle: 'bold'}, // Black text for 'Attribute'
                1: {
                    fillColor: [255, 255, 255],
                    textColor: [0, 0, 0],
                    font: 'NotoSansJPRegular',
                }, // Black text for 'Value'
            },
            margin: {horizontal: 10},
            theme: 'grid',
        });

        // Footer with color
        doc.setFontSize(10);
        if (
            row.status.toUpperCase() === PayoutStatus.INITIAL ||
            row.status.toUpperCase() === PayoutStatus.PENDING ||
            row.status.toUpperCase() === PayoutStatus.PROCESSING ||
            row.status.toUpperCase() === PayoutStatus.READY_TO_PAY ||
            row.status.toUpperCase() === PayoutStatus.QUEUING
        ) {
            doc.text('This payment is not yet completed.', 10, 275, {align: 'left'});
        } else if (
            row.status.toUpperCase() === PayoutStatus.FAILED ||
            row.status.toUpperCase() === PayoutStatus.UNPAID ||
            row.status.toUpperCase() === PayoutStatus.BLOCKED ||
            row.status.toUpperCase() === PayoutStatus.DERECOGNIZED
        ) {
            doc.text('This payment is failed to be completed.', 10, 275, {align: 'left'});
        }
        doc.text('This document is generated by Udemy.', 10, 280, {align: 'left'});

        // Save the PDF
        doc.save(`Payslip-${earningPeriodsString}.pdf`);
        // Logic to download the payslip goes here

        store.publishPayslipDownloadedEvent();
    };

    function getEarningPeriodsString(periods: string[]): string {
        return periods
            .map((item) => {
                const [month, year] = item.split('-');
                const date = new Date(parseInt(year), parseInt(month) - 1, 1);
                const monthStr = date.toLocaleString('default', {month: 'long'});
                return `${monthStr} ${year}`;
            })
            .join(',');
    }

    const getContent = () => {
        if (instructorPayoutsInfoPage.data?.instructorPayoutsByPage?.payouts) {
            return (
                <div className={styles['history-table-container']}>
                    <Table
                        padding="sm"
                        caption="Payout List"
                        columns={[
                            {
                                fieldName: 'created',
                                headerName: gettext('Created'),
                                renderMethod: (rowData) => {
                                    const row = rowData as PayoutTableItem;
                                    return toLocaleDateString(new Date(row.created.slice(0, 7)), {
                                        month: 'short',
                                        year: 'numeric',
                                    });
                                },
                                width: 100,
                            },
                            {
                                fieldName: 'status',
                                headerName: gettext('Status'),
                                renderMethod: (rowData) => {
                                    const row = rowData as PayoutTableItem;
                                    return (
                                        <PayoutStatusBadge
                                            status={row.status}
                                            payoutMethod={row.payoutMethodType}
                                        />
                                    );
                                },
                                width: 130,
                            },
                            {
                                fieldName: 'payoutMethodType',
                                headerName: gettext('Payout method'),
                                renderMethod: (rowData) => {
                                    const row = rowData as PayoutTableItem;
                                    return PayoutMethodMapping(row.payoutMethodType);
                                },
                                width: 150,
                            },
                            {
                                fieldName: 'amount',
                                headerName: gettext('Pre-tax amount'),
                                renderMethod: (rowData) => {
                                    const row = rowData as PayoutTableItem;
                                    return (
                                        <span>
                                            ${formatNumber((row.amount + row.taxAmount).toFixed(2))}
                                        </span>
                                    );
                                },
                                width: 150,
                            },
                            {
                                fieldName: 'taxAmount',
                                headerName: gettext('Withholding tax'),
                                renderMethod: (rowData) => {
                                    const row = rowData as PayoutTableItem;
                                    return <span>${formatNumber(row.taxAmount.toFixed(2))}</span>;
                                },
                                width: 150,
                            },
                            {
                                fieldName: 'net_amount',
                                headerName: gettext('Net earning'),
                                renderMethod: (rowData) => {
                                    const row = rowData as PayoutTableItem;
                                    return <span>${formatNumber(row.amount.toFixed(2))}</span>;
                                },
                                width: 150,
                            },
                            {
                                fieldName: 'download',
                                headerName: 'Payslip',
                                width: 50,
                                renderMethod: (rowData) => {
                                    const row = rowData as PayoutTableItem;
                                    return (
                                        <IconButton
                                            udStyle="white-solid"
                                            size="xsmall"
                                            aria-label="Download"
                                            onClick={() => downloadPayslip(row)}
                                        >
                                            <Icon size="large" glyph="file-pdf" label="Download" />
                                        </IconButton>
                                    );
                                },
                            },
                        ]}
                        rows={instructorPayoutsInfoPage.data?.instructorPayoutsByPage?.payouts}
                    />
                    {getLastPageNoteOldPayoutData(
                        instructorPayoutsInfoPage.data?.instructorPayoutsByPage?.hasOlderPayouts,
                        instructorPayoutsInfoPage.data?.instructorPayoutsByPage?.pageCount,
                        store.currentPage,
                    )}

                    <Pagination
                        pageCount={
                            instructorPayoutsInfoPage.data?.instructorPayoutsByPage?.pageCount
                        }
                        activePage={store.currentPage}
                        onPageChange={handlePageChange}
                        className={styles['pagination']}
                    />
                </div>
            );
        } else {
            return <MainContentLoader data-testid="loader" />;
        }
    };

    return (
        <div className={styles['section-container']}>
            <h1 className="ud-heading-xl">{gettext('Payout History')}</h1>
            {getContent()}
        </div>
    );
});
